/* KpiDrilldownModal — Modal genérico para detalhamento de cards do Dashboard.
 *
 * Recebe uma config { entity, filters, title, subtitle, columns } e renderiza
 * uma tabela com ações contextuais (Ver semente, Ver PDF da fonte, Promover insumo, etc.).
 */
import { useEffect, useState, useCallback } from "react";
import { api, API_BASE, formatErr } from "@/lib/api";
import { AREAS, CLASSIFICACOES, STATUS, labelOf } from "@/lib/constants";
import { X, ExternalLink, FileText, RefreshCcw, Eye, Sprout, Layers, Database, Download, Search } from "lucide-react";
import SeedDetailModal from "@/components/SeedDetailModal";
import SourceDetailModal from "@/components/SourceDetailModal";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

const statusBadge = (s) => {
  const def = STATUS.find((x) => x.value === s);
  return <span className={`badge ${def?.className || "badge-muted"}`}>{def?.label || s || "—"}</span>;
};

const ORIGEM_LABEL = {
  manual: "Manual", upload_ia: "Upload IA", automatica: "Coleta auto",
  legada: "Legada", insumo_v2: "V2", legada_enriquecida: "Legada+IA",
};

export default function KpiDrilldownModal({ config, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [openSeed, setOpenSeed] = useState(null);
  const [openSource, setOpenSource] = useState(null);

  const load = useCallback(async () => {
    if (!config) return;
    setLoading(true); setErr("");
    try {
      const params = { ...(config.filters || {}), limit: 500 };
      if (q) params.q = q;
      const { data } = await api.get(`/${config.entity}`, { params });
      // ordering hint
      let list = data || [];
      if (config.sortBy) {
        list = [...list].sort((a, b) => (b[config.sortBy] || 0) - (a[config.sortBy] || 0));
      }
      setRows(list);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [config, q]);

  useEffect(() => {
    if (config) {
      setRows([]); setQ("");
      load();
    }
  }, [config, load]);

  if (!config) return null;

  const { entity, title, subtitle, icon: Icon } = config;

  const exportCsv = () => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object" || Array.isArray(rows[0][k]));
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => {
        const v = r[h];
        if (Array.isArray(v)) return `"${v.join(";")}"`;
        if (v === null || v === undefined) return "";
        return `"${String(v).replace(/"/g, '""')}"`;
      }).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${entity}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="kpi-drilldown-overlay"
      >
        <div
          className="card-mil w-full max-w-6xl my-8 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="kpi-drilldown-modal"
        >
          {/* Header */}
          <div className="p-5 border-b border-[var(--border)] flex items-start justify-between gap-3 gold-accent-top">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon size={14} className="text-[var(--gold)]" />}
                <span className="label-mil text-[var(--gold)]">Detalhamento · {entity.toUpperCase()}</span>
              </div>
              <h2 className="font-display font-bold text-xl text-[var(--text)] leading-tight">{title}</h2>
              {subtitle && <p className="text-xs text-[var(--text-muted)] mt-1">{subtitle}</p>}
              <div className="text-[10px] font-mono text-[var(--text-muted)] mt-2">
                {loading ? "CARREGANDO…" : `${rows.length} REGISTROS`}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--surface-2)] rounded text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="kpi-drilldown-close">
              <X size={18} />
            </button>
          </div>

          {/* Toolbar */}
          <div className="p-3 border-b border-[var(--border)] flex flex-wrap items-center gap-2 bg-[var(--bg)]/40">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                className="input-mil pl-9 text-xs"
                placeholder="Filtrar por título, palavra-chave…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="kpi-drilldown-search"
              />
            </div>
            <button onClick={load} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="kpi-drilldown-refresh">
              <RefreshCcw size={12} /> Recarregar
            </button>
            <button onClick={exportCsv} disabled={rows.length === 0} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="kpi-drilldown-export">
              <Download size={12} /> CSV
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto scrollbar-thin">
            {err && <div className="m-4 text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded">{err}</div>}
            {!err && rows.length === 0 && !loading && (
              <div className="p-12 text-center text-[var(--text-muted)] text-sm">
                Nenhum registro corresponde a este indicador no momento.
              </div>
            )}
            {!err && rows.length > 0 && (
              <table className="w-full text-sm" data-testid="kpi-drilldown-table">
                <thead className="sticky top-0 bg-[var(--surface)] z-10">
                  <tr>
                    {entity === "seeds" && (
                      <>
                        <th className="px-4 py-2 label-mil text-left">Título</th>
                        <th className="px-4 py-2 label-mil text-left">Área</th>
                        <th className="px-4 py-2 label-mil text-left">Classif.</th>
                        <th className="px-4 py-2 label-mil text-left">Origem</th>
                        <th className="px-4 py-2 label-mil text-left">Status</th>
                        <th className="px-4 py-2 label-mil text-left">Cadastro</th>
                        <th className="px-4 py-2"></th>
                      </>
                    )}
                    {entity === "sources" && (
                      <>
                        <th className="px-4 py-2 label-mil text-left">Título</th>
                        <th className="px-4 py-2 label-mil text-left">Instituição</th>
                        <th className="px-4 py-2 label-mil text-left">Ano</th>
                        <th className="px-4 py-2 label-mil text-left">País</th>
                        <th className="px-4 py-2 label-mil text-left">Inserido por</th>
                        <th className="px-4 py-2 label-mil text-left">Arquivo</th>
                        <th className="px-4 py-2"></th>
                      </>
                    )}
                    {entity === "inputs" && (
                      <>
                        <th className="px-4 py-2 label-mil text-left">Insumo</th>
                        <th className="px-4 py-2 label-mil text-left">Fonte</th>
                        <th className="px-4 py-2 label-mil text-left">Status</th>
                        <th className="px-4 py-2 label-mil text-left">Score IA</th>
                        <th className="px-4 py-2 label-mil text-left">Criado</th>
                        <th className="px-4 py-2"></th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {entity === "seeds" && rows.map((s) => (
                    <tr key={s.seed_id} className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] cursor-pointer"
                        onClick={() => setOpenSeed(s.seed_id)} data-testid={`row-seed-${s.seed_id}`}>
                      <td className="px-4 py-2 max-w-[300px]">
                        <div className="font-semibold truncate">{s.titulo_insumo}</div>
                        <div className="text-[10px] font-mono text-[var(--text-muted)] truncate">{s.source_titulo}</div>
                      </td>
                      <td className="px-4 py-2 text-xs">{labelOf(AREAS, s.area_tematica_principal)}</td>
                      <td className="px-4 py-2"><span className="badge badge-gold">{labelOf(CLASSIFICACOES, s.classificacao_semente)}</span></td>
                      <td className="px-4 py-2"><span className="badge badge-muted text-[10px]">{ORIGEM_LABEL[s.origem_cadastro] || "—"}</span></td>
                      <td className="px-4 py-2">{statusBadge(s.status_validacao)}</td>
                      <td className="px-4 py-2 text-[10px] font-mono text-[var(--text-muted)]">{fmtDate(s.data_cadastro || s.created_at)}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={(e) => { e.stopPropagation(); setOpenSeed(s.seed_id); }}
                                className="btn-ghost text-xs flex items-center gap-1" data-testid={`action-view-seed-${s.seed_id}`}>
                          <Eye size={11} /> Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                  {entity === "sources" && rows.map((src) => (
                    <tr key={src.source_id}
                        className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] cursor-pointer"
                        onClick={() => setOpenSource(src.source_id)}
                        data-testid={`row-source-${src.source_id}`}>
                      <td className="px-4 py-2 max-w-[320px]">
                        <div className="font-semibold truncate">{src.titulo}</div>
                        <div className="text-[10px] font-mono text-[var(--text-muted)] truncate">
                          {(src.autores || []).slice(0, 3).join(", ")}{(src.autores || []).length > 3 ? "…" : ""}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs truncate max-w-[180px]">{src.instituicao || src.origem_fonte || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs">{src.ano_publicacao || "—"}</td>
                      <td className="px-4 py-2 text-xs">{src.pais_publicacao || "—"}</td>
                      <td className="px-4 py-2 text-xs truncate max-w-[160px]">
                        {src.imported_by_user_name || src.created_by_user_name || src.researcher_user_name || <span className="text-[var(--text-muted)]">—</span>}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {src.has_pdf && <span className="badge badge-success text-[10px]">PDF</span>}
                        {src.link_drive && <span className="badge badge-warn text-[10px] ml-1">DRIVE</span>}
                        {!src.has_pdf && !src.link_drive && <span className="text-[var(--text-muted)]">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {src.has_pdf && (
                          <a href={`${API_BASE}/sources/${src.source_id}/pdf`} target="_blank" rel="noreferrer"
                             className="btn-ghost text-xs flex items-center gap-1" data-testid={`action-pdf-source-${src.source_id}`}>
                            <FileText size={11} /> PDF
                          </a>
                        )}
                        {src.link_original && (
                          <a href={src.link_original} target="_blank" rel="noreferrer"
                             className="btn-ghost text-xs flex items-center gap-1" data-testid={`action-link-source-${src.source_id}`}>
                            <ExternalLink size={11} /> Link
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                  {entity === "inputs" && rows.map((inp) => (
                    <tr key={inp.input_id} className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)]" data-testid={`row-input-${inp.input_id}`}>
                      <td className="px-4 py-2 max-w-[300px]">
                        <div className="font-semibold truncate">{inp.titulo_insumo}</div>
                        <div className="text-[10px] font-mono text-[var(--text-muted)] truncate">{inp.input_id}</div>
                      </td>
                      <td className="px-4 py-2 text-xs truncate max-w-[200px]">{inp.source_id || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`badge ${inp.status === "promovido" ? "badge-success" : inp.status === "descartado" ? "badge-danger" : "badge-muted"}`}>
                          {inp.status || "rascunho"}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs">{inp.ai_confidence_score ?? "—"}</td>
                      <td className="px-4 py-2 text-[10px] font-mono text-[var(--text-muted)]">{fmtDate(inp.created_at)}</td>
                      <td className="px-4 py-2 text-right">
                        {inp.seed_id ? (
                          <button onClick={() => setOpenSeed(inp.seed_id)} className="btn-ghost text-xs flex items-center gap-1"
                                  data-testid={`action-view-seed-from-input-${inp.input_id}`}>
                            <Sprout size={11} /> Ver semente
                          </button>
                        ) : (
                          <span className="text-[10px] text-[var(--text-muted)] font-mono">não promovido</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-[var(--border)] flex justify-between items-center gap-2 bg-[var(--bg)]/40">
            <div className="text-[10px] font-mono text-[var(--text-muted)] flex items-center gap-1">
              {entity === "seeds" && <><Sprout size={10} /> SEMENTES</>}
              {entity === "sources" && <><Database size={10} /> FONTES</>}
              {entity === "inputs" && <><Layers size={10} /> INSUMOS</>}
              <span>· clique numa linha para abrir o detalhe completo</span>
            </div>
            <button onClick={onClose} className="btn-ghost text-xs" data-testid="kpi-drilldown-fechar">Fechar</button>
          </div>
        </div>
      </div>

      <SeedDetailModal seedId={openSeed} onClose={() => setOpenSeed(null)} onChanged={load} />
      <SourceDetailModal sourceId={openSource} onClose={() => setOpenSource(null)} />
    </>
  );
}
